import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Buildings, UsersFour, Barbell, ShieldCheck, Globe } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AcademyPublic() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/academies/public/${slug}`)
      .then(({ data }) => setData(data))
      .catch(() => setErr("Academy not found"));
  }, [slug]);

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" data-testid="academy-public-not-found">
        <div className="text-center">
          <div className="font-heading text-3xl mb-2">Academy not found</div>
          <Link to="/" className="text-brand-accent text-sm label-tiny">← BACK HOME</Link>
        </div>
      </div>
    );
  }

  if (!data) return <div className="p-10 label-tiny text-text-secondary" data-testid="academy-public-loading">Loading...</div>;

  const hero = data.hero_image?.startsWith("/api/")
    ? `${BACKEND_URL}${data.hero_image}`
    : data.hero_image;

  return (
    <div className="min-h-screen landing-dark" data-testid="academy-public-page">
      {/* Hero */}
      <div className="relative overflow-hidden">
        {hero ? (
          <img src={hero} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-brand-accent/40 via-brand-primary to-black" />
        )}
        <div className="relative max-w-5xl mx-auto p-8 lg:p-16 min-h-[420px] flex items-end">
          <div>
            <div className="inline-flex items-center gap-2 label-tiny text-white/70 mb-3">
              <Buildings size={14} weight="duotone" /> // ACADEMY
            </div>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl text-white" data-testid="academy-public-name">
              {data.name}
            </h1>
            {data.description && (
              <p className="text-white/80 mt-4 max-w-2xl text-lg" data-testid="academy-public-desc">
                {data.description}
              </p>
            )}
            {data.website && (
              <a
                href={data.website}
                target="_blank" rel="noreferrer"
                className="mt-4 inline-flex items-center gap-1 text-brand-accent text-sm label-tiny tracking-wider"
                data-testid="academy-public-website"
              >
                <Globe size={14} /> VISIT WEBSITE
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="max-w-5xl mx-auto px-8 lg:px-16 py-10 grid grid-cols-3 gap-4" data-testid="academy-public-stats">
        <PubStat icon={<UsersFour size={22} weight="duotone" />} label="COACHES" value={data.counts.coach} />
        <PubStat icon={<Barbell size={22} weight="duotone" />} label="ATHLETES" value={data.counts.athlete} />
        <PubStat icon={<ShieldCheck size={22} weight="duotone" />} label="ACTIVE" value={data.counts.total} />
      </div>

      {/* Coaches */}
      <div className="max-w-5xl mx-auto px-8 lg:px-16 pb-16">
        <div className="font-heading text-3xl text-white mb-6">Our Coaches</div>
        {data.coaches.length === 0 ? (
          <div className="text-white/60 text-sm">No coaches listed yet.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4" data-testid="academy-public-coaches">
            {data.coaches.map((c) => {
              const pic = c.picture?.startsWith("/api/") ? `${BACKEND_URL}${c.picture}` : c.picture;
              const inner = (
                <div className="border border-white/10 bg-white/5 p-5 hover:bg-white/10 transition h-full">
                  <div className="flex items-center gap-3">
                    {pic ? (
                      <img src={pic} alt="" className="w-14 h-14 rounded-full object-cover" />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-brand-accent/30 flex items-center justify-center text-lg font-bold text-white">
                        {(c.name || "?")[0].toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="font-heading text-lg text-white truncate">{c.name}</div>
                      {c.specialty && (
                        <div className="text-white/70 text-xs truncate">{c.specialty}</div>
                      )}
                    </div>
                  </div>
                  {c.bio && <div className="mt-3 text-white/70 text-sm line-clamp-3">{c.bio}</div>}
                </div>
              );
              return c.slug ? (
                <Link key={c.user_id} to={`/c/${c.slug}`} data-testid={`academy-coach-card-${c.user_id}`}>{inner}</Link>
              ) : (
                <div key={c.user_id} data-testid={`academy-coach-card-${c.user_id}`}>{inner}</div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const PubStat = ({ icon, label, value }) => (
  <div className="border border-white/10 bg-white/5 p-5">
    <div className="text-brand-accent mb-2">{icon}</div>
    <div className="font-heading text-3xl text-white tabular-nums">{value}</div>
    <div className="label-tiny text-[10px] text-white/60 mt-1">{label}</div>
  </div>
);
